import {
  DEFAULT_TEMPLATE_FOR_PRODUCT,
  isDiarizationProvider,
  isEnsembleMergeMode,
  isTranscriptionMode,
  type TDiarizationProvider,
  type TEnsembleMergeMode,
  type TTranscriptionMode,
} from "@lexicanon/shared"
import { msg } from "@lingui/core/macro"
import { useLingui } from "@lingui/react"
import { Trans } from "@lingui/react/macro"
import {
  Alert,
  Badge,
  Button,
  Checkbox,
  Group,
  Modal,
  Radio,
  Select,
  Stack,
  Switch,
  Text,
  Textarea,
} from "@mantine/core"
import { IconAlertTriangle } from "@tabler/icons-react"
import { useCallback, useEffect, useMemo, useState } from "react"
import { templateLabel, templateOptions } from "../../../lib/template-labels"
import type { IAdapterInfo } from "../../../lib/types/api"
import { formatEnsembleCreditsLine, type IEnsembleEstimateBreakdown } from "../helpers"
import classes from "../styles.module.css"
import type { IViewProps, TReprocessMode } from "./interfaces"

/** Sentinel Select value for "use the workspace setting" — omitted from the POST. */
const WORKSPACE_DIARIZATION = "workspace"
const WORKSPACE_ENGINES = "workspace"
const WORKSPACE_MERGE = "workspace"

/** Relative cost tier for a reprocess mode — drives the per-mode cost badge. */
type TCreditsTier = "high" | "medium" | "low" | "medium" | "low"

/**
 * Dumb view for ReprocessForm. Owns the local form state (template, mode,
 * adapter list, prompt draft, open flag) and fires `onSubmit({...})` to the
 * smart wrapper. The wrapper owns the API calls + notifications.
 *
 * Adapter list and default prompt are loaded lazily via async loader props
 * the wrapper provides — the view caches the results so we don't refetch
 * while the modal is open.
 */
export function ReprocessFormView({
  templateId,
  hasRecording,
  hasManualEdits = false,
  loading,
  opened: controlledOpened,
  onLoadAdapters,
  onLoadDefaultPrompt,
  onSubmit,
  onClose: controlledOnClose,
  workspaceDiarizationProvider = "deepgram",
  workspaceTranscriptionMode = "ensemble",
  workspaceMergeMode = "constrained",
  meetingLanguage,
  onEstimate,
}: IViewProps): React.JSX.Element {
  // 1. STATE
  const isControlled = controlledOpened !== undefined
  const [internalOpened, setInternalOpened] = useState(false)
  const opened = isControlled ? controlledOpened : internalOpened
  const [template, setTemplate] = useState(templateId ?? DEFAULT_TEMPLATE_FOR_PRODUCT.lexicanon)
  const [mode, setMode] = useState<TReprocessMode>(
    (templateId ?? DEFAULT_TEMPLATE_FOR_PRODUCT.lexicanon) === "meeting-lite" ? "speakers" : "full",
  )
  const [adapter, setAdapter] = useState<string | null>(null)
  const [diarizationProvider, setDiarizationProvider] = useState<TDiarizationProvider | null>(null)
  const [transcriptionMode, setTranscriptionMode] = useState<TTranscriptionMode | null>(null)
  const [mergeMode, setMergeMode] = useState<TEnsembleMergeMode | null>(null)
  const [remerge, setRemerge] = useState(false)
  const [adapters, setAdapters] = useState<IAdapterInfo[]>([])
  const [customizePrompt, setCustomizePrompt] = useState(false)
  const [prompt, setPrompt] = useState("")
  const [promptLoaded, setPromptLoaded] = useState(false)
  const [forceImages, setForceImages] = useState(false)

  // 2. EXTERNAL HOOKS
  const { i18n } = useLingui()

  // 3. CALLBACKS
  const handleOpen = useCallback((): void => {
    setInternalOpened(true)
  }, [])

  const handleClose = useCallback((): void => {
    if (loading) return
    setInternalOpened(false)
    setCustomizePrompt(false)
    setPrompt("")
    setPromptLoaded(false)
    setAdapter(null)
    setDiarizationProvider(null)
    setTranscriptionMode(null)
    setMergeMode(null)
    setRemerge(false)
    setMode(template === "meeting-lite" ? "speakers" : "full")
    setForceImages(false)
    controlledOnClose?.()
  }, [loading, controlledOnClose, template])

  const handleTemplateChange = useCallback(
    (value: string | null): void => {
      const next = value ?? DEFAULT_TEMPLATE_FOR_PRODUCT.lexicanon
      setTemplate(next)
      if (next === "meeting-lite") setMode("speakers")
      else if (template === "meeting-lite") setMode("full")
      // Reset prompt when template changes so it re-fetches if customization is on
      if (customizePrompt) {
        setPrompt("")
        setPromptLoaded(false)
      }
    },
    [customizePrompt, template],
  )

  const handleCustomizeToggle = useCallback((event: React.ChangeEvent<HTMLInputElement>): void => {
    const checked = event.currentTarget.checked
    setCustomizePrompt(checked)
    if (!checked) {
      setPrompt("")
      setPromptLoaded(false)
    }
  }, [])

  const handleSubmit = useCallback(async (): Promise<void> => {
    // systemPrompt only applies to the legacy "full" path. The graph
    // executor uses the template's prompt for each step; the override here
    // would be silently ignored on non-full modes.
    const useCustomPrompt = mode === "full" && customizePrompt && Boolean(prompt)
    try {
      await onSubmit({
        template,
        mode,
        adapter,
        diarizationProvider: diarizationProvider ?? undefined,
        transcriptionMode: transcriptionMode ?? undefined,
        mergeMode: mergeMode ?? undefined,
        remerge: remerge || undefined,
        systemPrompt: useCustomPrompt ? prompt : null,
        // Only meaningful in images mode; the wrapper ignores it otherwise.
        forceImages: mode === "images" && forceImages,
      })
      handleClose()
    } catch {
      // Wrapper surfaces the error via notifications; keep the modal open
      // so the user can adjust + retry.
    }
  }, [
    template,
    mode,
    adapter,
    diarizationProvider,
    transcriptionMode,
    mergeMode,
    remerge,
    customizePrompt,
    prompt,
    forceImages,
    onSubmit,
    handleClose,
  ])

  // 4. EFFECTS
  useEffect(() => {
    if (!opened) return
    if (hasRecording && adapters.length === 0) {
      // Swallow a failed load: leave the adapter list empty so the
      // re-transcribe options stay hidden (the user can still re-analyze).
      // The smart wrapper surfaces the error; an uncaught rejection here would
      // otherwise bubble as an unhandled promise rejection.
      void onLoadAdapters()
        .then((list) => setAdapters(list))
        .catch(() => {})
    }
  }, [opened, hasRecording, adapters.length, onLoadAdapters])

  useEffect(() => {
    if (!opened || !customizePrompt || promptLoaded) return
    void onLoadDefaultPrompt()
      .then((p) => {
        setPrompt(p)
        setPromptLoaded(true)
      })
      .catch(() => {})
  }, [opened, customizePrompt, promptLoaded, onLoadDefaultPrompt])

  // 5. DERIVED
  const isDnd = template === "dnd-companion"
  const isLite = template === "meeting-lite"
  const templates = useMemo(() => {
    const opts = templateOptions(i18n)
    if (template === "meeting-lite" && !opts.some((o) => o.value === "meeting-lite")) {
      return [{ value: "meeting-lite" as const, label: templateLabel(i18n, "meeting-lite") }, ...opts]
    }
    return opts
  }, [i18n, template])
  const modalTitle = isDnd ? i18n._(msg`Reprocess session`) : i18n._(msg`Reprocess meeting`)
  // A full pipeline rerun re-transcribes (when audio exists), and so does an
  // explicit speaker-turns override or a chosen transcription engine — mirror
  // the backend's reTranscribe rule so the button says what will happen.
  const willRetranscribe =
    mode === "full" ||
    Boolean(adapter) ||
    diarizationProvider !== null ||
    transcriptionMode !== null ||
    mergeMode !== null ||
    remerge
  const effectiveTranscriptionMode: TTranscriptionMode = transcriptionMode ?? workspaceTranscriptionMode
  const showEnsembleControls = hasRecording && effectiveTranscriptionMode === "ensemble"
  const isDutch = Boolean(meetingLanguage?.toLowerCase().startsWith("nl"))

  // Pre-click credit estimate for the current choices (debounced; null = no suffix).
  const [estimateCredits, setEstimateCredits] = useState<number | null>(null)
  const [ensembleBreakdown, setEnsembleBreakdown] = useState<IEnsembleEstimateBreakdown | null>(null)
  useEffect(() => {
    if (!opened || !onEstimate) return
    let cancelled = false
    setEstimateCredits(null)
    setEnsembleBreakdown(null)
    const timer = setTimeout(() => {
      void onEstimate({
        mode,
        adapter,
        diarizationProvider: diarizationProvider ?? undefined,
        transcriptionMode: transcriptionMode ?? undefined,
        mergeMode: mergeMode ?? undefined,
        remerge: remerge || undefined,
      }).then((r) => {
        if (cancelled) return
        setEstimateCredits(r?.credits ?? null)
        setEnsembleBreakdown(r?.ensemble ?? null)
      })
    }, 250)
    return (): void => {
      cancelled = true
      clearTimeout(timer)
    }
  }, [opened, mode, adapter, diarizationProvider, transcriptionMode, mergeMode, remerge, onEstimate])

  const baseSubmitLabel = willRetranscribe ? i18n._(msg`Re-transcribe & analyze`) : i18n._(msg`Re-analyze`)
  const submitLabel =
    estimateCredits !== null && estimateCredits > 0 ? `${baseSubmitLabel} · ~${estimateCredits} cr` : baseSubmitLabel

  const workspaceProviderName = ((): string => {
    switch (workspaceDiarizationProvider) {
      case "deepgram":
        return i18n._(msg`Deepgram`)
      case "mai":
        return i18n._(msg`MAI-Transcribe-2`)
      case "azure-llm":
        return i18n._(msg`Azure LLM-speech`)
      case "assemblyai":
        return i18n._(msg`AssemblyAI Universal-3.5 Pro`)
      case "local":
        return i18n._(msg`Local service`)
      case "same":
        return i18n._(msg`Same provider as the words`)
    }
  })()
  const workspaceOptionLabel = `${i18n._(msg`Workspace default`)} · ${workspaceProviderName}`
  const workspaceModeName = workspaceTranscriptionMode === "ensemble" ? i18n._(msg`Ensemble`) : i18n._(msg`Two-pass`)
  const workspaceEnginesLabel = `${i18n._(msg`Workspace default`)} · ${workspaceModeName}`
  const workspaceMergeName = workspaceMergeMode === "rewrite" ? i18n._(msg`Rewrite`) : i18n._(msg`Constrained`)
  const workspaceMergeLabel = `${i18n._(msg`Workspace default`)} · ${workspaceMergeName}`
  const canRemerge = ensembleBreakdown?.canRemerge === true
  const ensembleCreditsLine = ensembleBreakdown ? formatEnsembleCreditsLine(ensembleBreakdown) : null

  useEffect(() => {
    if (showEnsembleControls) return
    setMergeMode(null)
    setRemerge(false)
  }, [showEnsembleControls])
  const promptToggleDisabled = loading || mode !== "full" || isLite

  // Per-mode credits signal — makes credits a consistent visible dimension so "Full
  // pipeline" reads as expensive vs the cheap chapter-only mode. `tone` is a
  // semantic palette name (theming rule 6).
  const creditsBadge = useCallback(
    (cost: TCreditsTier): React.JSX.Element => {
      const tone = cost === "high" ? "warning" : cost === "medium" ? "info" : "success"
      const label =
        cost === "high"
          ? i18n._(msg`More credits`)
          : cost === "medium"
            ? i18n._(msg`Some credits`)
            : i18n._(msg`Few credits`)
      return (
        <Badge size="xs" variant="light" color={tone}>
          {label}
        </Badge>
      )
    },
    [i18n],
  )

  // Selecting a transcription adapter or a speaker-turns override re-runs the
  // (expensive) transcription step, so it bumps the speaker-resolution path
  // from medium to high.
  const speakersCredits: TCreditsTier = willRetranscribe ? "high" : "medium"

  // 6. RENDER
  return (
    <>
      {!isControlled && (
        <Button variant="light" onClick={handleOpen}>
          <Trans>Reprocess</Trans>
        </Button>
      )}

      <Modal opened={opened} onClose={handleClose} title={modalTitle} centered size="lg">
        <Stack gap="md">
          {hasManualEdits && (
            <Alert
              color="warning"
              variant="light"
              icon={<IconAlertTriangle size={16} />}
              title={i18n._(msg`Your manual edits will be lost`)}
            >
              <Text size="sm">
                <Trans>
                  Reprocessing replaces the AI-generated fields — any manual edits you&rsquo;ve made to this analysis
                  will be overwritten.
                </Trans>
              </Text>
            </Alert>
          )}

          <Select
            label={i18n._(msg`Template`)}
            description={i18n._(msg`Determines which analysis profile is used`)}
            data={templates}
            value={template}
            onChange={handleTemplateChange}
            disabled={loading}
          />

          <Radio.Group
            label={i18n._(msg`What to re-run`)}
            description={i18n._(
              msg`Pick a starting step. The graph executor re-runs that step plus everything that depends on it; sibling artifacts stay as they are.`,
            )}
            value={mode}
            onChange={(v) => setMode(v as TReprocessMode)}
          >
            <Stack gap="xs" mt="xs">
              {!isLite && (
                <Radio
                  value="full"
                  label={
                    <Group gap="xs" wrap="nowrap">
                      <Trans>Full pipeline</Trans>
                      {creditsBadge("high")}
                    </Group>
                  }
                  description={i18n._(
                    msg`Re-runs every step from scratch — re-transcribes + re-analyzes and uses the most credits. Use the custom prompt below to tweak the system prompt.`,
                  )}
                  disabled={loading}
                />
              )}
              <Radio
                value="speakers"
                label={
                  <Group gap="xs" wrap="nowrap">
                    <Trans>From speaker identification</Trans>
                    {creditsBadge(speakersCredits)}
                  </Group>
                }
                description={
                  isLite
                    ? i18n._(
                        msg`Re-runs speaker identification on this recording. Names you typed are kept as suggestions, matched by voice where possible.`,
                      )
                    : i18n._(
                        msg`Names you typed are kept: they come back as suggestions on the new speakers, matched by voice where possible. Edits to the summary text are replaced.`,
                      )
                }
                disabled={loading}
              />
              {!isLite && (
                <Radio
                  value="analysis"
                  label={
                    <Group gap="xs" wrap="nowrap">
                      <Trans>From main analysis</Trans>
                      {creditsBadge("medium")}
                    </Group>
                  }
                  description={
                    isDnd
                      ? i18n._(
                          msg`Skip speaker work; redo the analysis and all reports / images. Existing speaker names stay. Uses credits.`,
                        )
                      : i18n._(
                          msg`Skip speaker work; redo the analysis, chapter ribbon, and markdown export. Existing speaker names stay. Uses credits.`,
                        )
                  }
                  disabled={loading}
                />
              )}
              {!isLite && (
                <Radio
                  value="chapters"
                  label={
                    <Group gap="xs" wrap="nowrap">
                      <Trans>Chapter ribbon only</Trans>
                      {creditsBadge("low")}
                    </Group>
                  }
                  description={i18n._(
                    msg`Re-cluster chapters from the existing progressive summaries. Cheapest + fastest — useful for backfilling chapters on older meetings.`,
                  )}
                  disabled={loading}
                />
              )}
              {isDnd && (
                <Radio
                  value="images"
                  label={
                    <Group gap="xs" wrap="nowrap">
                      <Trans>Scene & character images only</Trans>
                      {creditsBadge("medium")}
                    </Group>
                  }
                  description={i18n._(
                    msg`Re-generate scene illustrations and character portraits from the existing analysis. Skips everything else. Uses image credits.`,
                  )}
                  disabled={loading}
                />
              )}
            </Stack>
          </Radio.Group>

          {isDnd && mode === "images" && (
            <Switch
              label={i18n._(msg`Regenerate every image from scratch`)}
              description={i18n._(
                msg`Off: only fill in missing images (cheaper). On: delete and recreate all scene + character images.`,
              )}
              checked={forceImages}
              onChange={(e) => setForceImages(e.currentTarget.checked)}
              disabled={loading}
            />
          )}

          {hasRecording && (
            <div>
              <Select
                label={i18n._(msg`Transcription backend`)}
                description={
                  adapter === "soniox" && isDutch
                    ? i18n._(msg`Recommended for Dutch`)
                    : i18n._(msg`Leave empty to keep the current transcript`)
                }
                placeholder={i18n._(msg`Keep current transcript`)}
                data={adapters.map((a) => ({
                  value: a.name,
                  label:
                    a.name === "soniox" && isDutch
                      ? `${a.label} ${i18n._(msg`(re-transcribe)`)} — ${i18n._(msg`recommended for Dutch`)}`
                      : `${a.label} ${i18n._(msg`(re-transcribe)`)}`,
                }))}
                value={adapter}
                onChange={setAdapter}
                clearable
                disabled={loading}
              />
              {adapter && (
                <Text size="xs" c="dimmed" mt={4}>
                  <Trans>
                    Audio will be re-transcribed with {adapters.find((a) => a.name === adapter)?.label} before analysis.
                  </Trans>
                </Text>
              )}
              <Select
                label={i18n._(msg`Engines`)}
                description={
                  transcriptionMode === null
                    ? workspaceEnginesLabel
                    : i18n._(msg`Leave on workspace default unless you want a different path for this run.`)
                }
                data={[
                  { value: WORKSPACE_ENGINES, label: workspaceEnginesLabel },
                  { value: "two-pass", label: i18n._(msg`Two-pass`) },
                  { value: "ensemble", label: i18n._(msg`Ensemble`) },
                ]}
                value={transcriptionMode ?? WORKSPACE_ENGINES}
                onChange={(value) => {
                  if (!value || value === WORKSPACE_ENGINES) {
                    setTranscriptionMode(null)
                    return
                  }
                  if (isTranscriptionMode(value)) setTranscriptionMode(value)
                }}
                allowDeselect={false}
                disabled={loading}
                mt="sm"
              />
              {showEnsembleControls && (
                <Select
                  label={i18n._(msg`Merge behaviour`)}
                  description={
                    mergeMode === null
                      ? workspaceMergeLabel
                      : i18n._(
                          msg`Constrained never adds words that no engine heard. Rewrite uses more credits and can change wording.`,
                        )
                  }
                  data={[
                    { value: WORKSPACE_MERGE, label: workspaceMergeLabel },
                    {
                      value: "constrained",
                      label: i18n._(msg`Constrained — only choose between what the engines heard (recommended)`),
                    },
                    {
                      value: "rewrite",
                      label: i18n._(msg`Rewrite — let the AI reword disputed passages`),
                    },
                  ]}
                  value={mergeMode ?? WORKSPACE_MERGE}
                  onChange={(value) => {
                    if (!value || value === WORKSPACE_MERGE) {
                      setMergeMode(null)
                      return
                    }
                    if (isEnsembleMergeMode(value)) setMergeMode(value)
                  }}
                  allowDeselect={false}
                  disabled={loading}
                  mt="sm"
                />
              )}
              {showEnsembleControls && (
                <Checkbox
                  mt="sm"
                  label={i18n._(msg`Re-merge only — no new engine calls`)}
                  description={
                    canRemerge
                      ? i18n._(msg`Uses the stored engine results from last time. You still pay for the merge.`)
                      : i18n._(msg`Available when this meeting already has stored engine results.`)
                  }
                  checked={remerge && canRemerge}
                  disabled={loading || !canRemerge}
                  onChange={(e) => setRemerge(e.currentTarget.checked)}
                />
              )}
              <Select
                label={i18n._(msg`Speaker turns from`)}
                description={
                  diarizationProvider === null
                    ? workspaceOptionLabel
                    : i18n._(
                        msg`Leave on workspace default unless you want a different speaker-turn provider for this run. A second provider pass uses more credits.`,
                      )
                }
                data={[
                  { value: WORKSPACE_DIARIZATION, label: workspaceOptionLabel },
                  { value: "deepgram", label: i18n._(msg`Deepgram`) },
                  { value: "mai", label: i18n._(msg`MAI-Transcribe-2 (preview)`) },
                  { value: "azure-llm", label: i18n._(msg`Azure LLM-speech`) },
                  { value: "assemblyai", label: i18n._(msg`AssemblyAI Universal-3.5 Pro`) },
                  { value: "same", label: i18n._(msg`Same provider as the words`) },
                ]}
                value={diarizationProvider ?? WORKSPACE_DIARIZATION}
                onChange={(value) => {
                  if (!value || value === WORKSPACE_DIARIZATION) {
                    setDiarizationProvider(null)
                    return
                  }
                  if (isDiarizationProvider(value)) setDiarizationProvider(value)
                }}
                allowDeselect={false}
                disabled={loading}
                mt="sm"
              />
            </div>
          )}

          {!isLite && (
            <Switch
              label={i18n._(msg`Customize prompt`)}
              description={
                mode === "full"
                  ? i18n._(msg`Override the template's default system prompt`)
                  : i18n._(msg`Custom prompt only applies when "What to re-run" is set to Full pipeline`)
              }
              checked={customizePrompt && mode === "full"}
              onChange={handleCustomizeToggle}
              disabled={promptToggleDisabled}
            />
          )}

          {customizePrompt && mode === "full" && (
            <Textarea
              label={i18n._(msg`System prompt`)}
              placeholder={promptLoaded ? undefined : i18n._(msg`Loading default prompt…`)}
              value={prompt}
              onChange={(e) => setPrompt(e.currentTarget.value)}
              minRows={8}
              autosize
              disabled={loading || !promptLoaded}
              classNames={{ input: classes.promptTextarea }}
            />
          )}

          {ensembleCreditsLine && (
            <Text size="sm" c="dimmed">
              {ensembleCreditsLine}
            </Text>
          )}

          <Group justify="flex-end">
            <Button variant="subtle" onClick={handleClose} disabled={loading}>
              <Trans>Cancel</Trans>
            </Button>
            <Button onClick={handleSubmit} loading={loading}>
              {submitLabel}
            </Button>
          </Group>
        </Stack>
      </Modal>
    </>
  )
}
